import React, { useEffect, useState } from "react";
import { PENCIL, PROFILE } from "../../assets";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { selectUser, setUser } from "../../states/slices/authReducer";
import ButtonV2 from "../../shared/components/buttonV2";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { ProfileUpdateTypes } from "../../types";
import { profileUpdate } from "../../Services/auth";
import { toast } from "react-hot-toast";

function Settings() {
  const user = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowComfirmPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formField, setFormField] = useState({
    name: "",
    dob: "",
    currentPassword: "",
    newpassword: "",
    confirmNewpassword: "",
  });

  const handleinputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormField((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getPasswordPayloads = () => {
    if (formField.newpassword && formField.confirmNewpassword) {
      return {
        current_password: formField.currentPassword,
        password: formField.newpassword,
        password_confirmation: formField.confirmNewpassword,
      };
    } else return null;
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    const passwordPayload = getPasswordPayloads();
    const payload = {
      ...passwordPayload,
      name: formField.name,
      dob: formField.dob,
    };

    try {
      const response = await profileUpdate(payload);
      if (response) {
        toast.success(response?.message);
        dispatch(setUser(response?.data));
        if (
          formField.currentPassword &&
          formField.newpassword &&
          formField.confirmNewpassword
        ) {
          setFormField({
            ...formField,
            confirmNewpassword: "",
            newpassword: "",
            currentPassword: "",
          });
        }
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (user) {
      setFormField({ ...formField, name: user?.name, dob: user?.dob });
    }
  }, []);

  return (
    <div className="p-3 sm:p-5">
      <div className="flex items-start flex-col">
        <span className="text-[#2A2E34] font-semibold text-xl sm:text-2xl">
          Account Settings
        </span>
        <span className="text-primary-50 font-medium text-sm">
          Manage your personal information and preferences
        </span>
      </div>

      <div className="w-full flex flex-col lg:flex-row items-start space-y-5 lg:space-y-0 lg:space-x-5 mt-4">
        <div className="w-full lg:w-[350px] items-center justify-center flex flex-col gap-y-2 h-auto lg:h-[256px] border border-[#E9EAEB] rounded-xl p-4">
          <img
            src={PROFILE}
            className="w-16 h-16 sm:w-[100px] sm:h-[100px]"
            alt=""
          />
          <div className="flex flex-col gap-y-1 items-center">
            <span className="text-base sm:text-lg font-semibold text-customDark text-center">
              {user?.name?.replace(/_/g, " ")}
            </span>
            <span className="text-xs font-medium text-primary-50 text-center">
              {user?.email}
            </span>
          </div>
          <div className="text-xs font-medium bg-[#F3FAF3] text-[#00A63E] w-fit h-fit px-1.5 py-1 rounded">
            Active
          </div>
        </div>

        <div className="flex flex-col items-start gap-y-3 w-full">
          <div className="w-full border border-[#E9EAEB] rounded-xl">
            <div className="w-full py-3 px-2 bg-lightGold rounded-t-xl text-sm sm:text-[16px] font-semibold text-[#2A2E34]">
              Account Info
            </div>
            <div className="w-full flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4">
              <div className="flex flex-col gap-y-2 w-full">
                <span className="text-[#4F5762] font-medium text-sm">
                  Username
                </span>
                <div className="flex items-center gap-x-2 border border-borderColor rounded-lg p-2">
                  <input
                    type="text"
                    name="name"
                    value={formField.name}
                    onChange={handleinputChange}
                    className="w-full outline-none bg-transparent text-sm"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-y-2 w-full">
                <span className="text-[#4F5762] font-medium text-sm">
                  Date of Birth
                </span>
                <div className="flex items-center gap-x-2 border border-borderColor rounded-lg p-2">
                  <input
                    type="date"
                    name="dob"
                    defaultValue={formField.dob}
                    onChange={handleinputChange}
                    className="w-full outline-none bg-transparent text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="w-full border border-[#E9EAEB] rounded-xl">
            <div className="w-full py-3 px-2 bg-lightGold rounded-t-xl text-sm sm:text-[16px] font-semibold text-[#2A2E34]">
              Password Management
            </div>
            <div className="w-full grid grid-cols-1 lg:grid-cols-2 items-start gap-4 p-4">
              <div className="flex flex-col gap-y-2 w-full">
                <span className="text-[#4F5762] font-medium text-sm">
                  Current Password
                </span>
                <div className="flex items-center gap-x-2 border border-borderColor rounded-lg p-2">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    placeholder="******************"
                    name="currentPassword"
                    value={formField.currentPassword}
                    onChange={handleinputChange}
                    className="w-full outline-none bg-transparent text-sm"
                  />
                  <span
                    className="cursor-pointer text-gray-500 flex-shrink-0"
                    onClick={() => setShowCurrentPassword((prev) => !prev)}
                  >
                    {showCurrentPassword ? (
                      <FaEyeSlash size={16} />
                    ) : (
                      <FaEye size={16} />
                    )}
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-y-2 w-full">
                <span className="text-[#4F5762] font-medium text-sm">
                  New Password
                </span>
                <div className="flex items-center gap-x-2 border border-borderColor rounded-lg p-2">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    placeholder="******************"
                    name="newpassword"
                    value={formField.newpassword}
                    onChange={handleinputChange}
                    className="w-full outline-none bg-transparent text-sm"
                  />
                  <span
                    className="cursor-pointer text-gray-500 flex-shrink-0"
                    onClick={() => setShowNewPassword((prev) => !prev)}
                  >
                    {showNewPassword ? (
                      <FaEyeSlash size={16} />
                    ) : (
                      <FaEye size={16} />
                    )}
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-y-2 w-full lg:col-span-2">
                <span className="text-[#4F5762] font-medium text-sm">
                  Confirm Password
                </span>
                <div className="flex items-center gap-x-2 border border-borderColor rounded-lg p-2">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="******************"
                    name="confirmNewpassword"
                    value={formField.confirmNewpassword}
                    onChange={handleinputChange}
                    className="w-full outline-none bg-transparent text-sm"
                  />
                  <span
                    className="cursor-pointer text-gray-500 flex-shrink-0"
                    onClick={() => setShowComfirmPassword((prev) => !prev)}
                  >
                    {showConfirmPassword ? (
                      <FaEyeSlash size={16} />
                    ) : (
                      <FaEye size={16} />
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full flex items-center justify-center sm:justify-end">
            <ButtonV2
              title={"Save Changes"}
              loading={saving}
              loaderColor="#ffffff"
              btnStyle={
                "bg-globalGold w-full sm:w-fit h-fit px-6 py-2 rounded-lg"
              }
              textStyle={"text-sm font-medium text-white"}
              handleClick={handleSaveChanges}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;
